import React, { useState, useEffect } from "react";
import {
  CCard,
  CCardBody,
  CTabs,
  CTab,
  CTabList,
  CTabPanel,
  CTabContent,
  CSpinner,
} from "@coreui/react";
import { useNavigate } from "react-router-dom";
import Skeletons from "./Skeletons";
import BarChart from "./BarChart";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";
import {
  getProfile,
  getDashboardRegisteredUsers,
  getDashboardLoggedInUsers,
  getDashboardIncompleteProfileUsers,
  getDashboardLiveLoggedInUsers,
  getDashboardTotalOrders,
  getDashboardTotalPolicies,
  getDashboardTotalSales,
  getDashboardTotalByPaymentMethod,
  getDashboardTransactionsByInsurerProduct,
  getDashboardPolicyOutcomes,
  getDashboardTransactionVolume,
  getDashboardTransactionAmounts,
  getDashboardPaymentMethodOutcomes,
} from "../../utils/backend";
import "@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css";
import "react-calendar/dist/Calendar.css";
import WelcomeCard from "./WelcomeCard";
import RegisteredUsersCard from "./RegisteredUsersCard";
import IncompleteProfilesCard from "./IncompleteProfilesCard";
import LiveLoggedInUsersCard from "./LiveLoggedInUsersCard";
import SalesCard from "./SalesCard";
import OrdersCard from "./OrdersCard";
import PoliciesCard from "./PoliciesCard";
import PaymentMethodCard from "./PaymentMethodCard";
import QuotationFunnelCard from "./QuotationFunnelCard";
import TransactionBreakdownCard from "./TransactionBreakdownCard";
import PolicyOutcomeCard from "./PolicyOutcomeCard";
import TransactionVolumeCard from "./TransactionVolumeCard";
import TransactionAmountCard from "./TransactionAmountCard";
import UnlockAmountsModal from "./UnlockAmountsModal";
import {
  getElevationToken,
  getElevationRemainingMs,
  clearElevation,
} from "../../utils/elevation";
const Dashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);
  const [admin, setAdmin] = useState({});
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [customChartData, setCustomChartData] = useState({
    labels: [],
    datasets: [],
  });
  const [customLoggedInChartData, setCustomLoggedInChartData] = useState({
    labels: [],
    datasets: [],
  });
  const [totalLoggedIn, setTotalLoggedIn] = useState(0);
  const [loggedInStatus, setLoggedInStatus] = useState("");
  const [loggedInPercentageChange, setLoggedInPercentageChange] = useState(0);
  const [totalRegistered, setTotalRegistered] = useState(0);
  const [registerStatus, setRegisterStatus] = useState("");
  const [registerPercentageChange, setRegisterPercentageChange] = useState(0);
  const [incompleteProfiles, setIncompleteProfiles] = useState(null);
  const [incompleteProfilesLoading, setIncompleteProfilesLoading] =
    useState(true);
  const [incompleteProfilesError, setIncompleteProfilesError] = useState(null);
  const [liveLoggedIn, setLiveLoggedIn] = useState(null);
  const [liveLoggedInLoading, setLiveLoggedInLoading] = useState(true);
  const [liveLoggedInError, setLiveLoggedInError] = useState(null);

  const getStartAndEndOfMonth = () => {
    const start = new Date();
    start.setDate(1);
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setMonth(end.getMonth() + 1);
    end.setDate(0);
    end.setHours(23, 59, 59, 999);

    return [start, end];
  };

  const getStartAndEndOfFiveMonthsAgoToEndOfMonth = () => {
    const start = new Date();
    start.setMonth(start.getMonth() - 5);
    start.setDate(1);
    start.setHours(0, 0, 0, 0);

    const end = new Date();
    end.setMonth(end.getMonth() + 1);
    end.setDate(0);
    end.setHours(23, 59, 59, 999);

    return [start, end];
  };

  const [dateRange, setDateRange] = useState(
    getStartAndEndOfFiveMonthsAgoToEndOfMonth()
  );
  const [loggedInDateRange, setLoggedInDateRange] = useState(
    getStartAndEndOfFiveMonthsAgoToEndOfMonth()
  );
  const [salesData, setSalesData] = useState({ labels: [], datasets: [] });
  const [ordersData, setOrdersData] = useState({ labels: [], datasets: [] });
  const [ordersDateRange, setOrdersDateRange] = useState(
    getStartAndEndOfFiveMonthsAgoToEndOfMonth()
  );
  const [policiesData, setPoliciesData] = useState({
    labels: [],
    datasets: [],
  });
  const [policiesDateRange, setPoliciesDateRange] = useState(
    getStartAndEndOfFiveMonthsAgoToEndOfMonth()
  );
  const [salesDateRange, setSalesDateRange] = useState(
    getStartAndEndOfFiveMonthsAgoToEndOfMonth()
  );
  const [totalSales, setTotalSales] = useState(0);
  const [salesStatus, setSalesStatus] = useState("");
  const [percentageChange, setPercentageChange] = useState(0);
  const [totalPreviousSales, setTotalPreviousSales] = useState(0);
  const [paymentMethodData, setPaymentMethodData] = useState({
    labels: [],
    datasets: [],
  });
  const [paymentMethodDateRange, setPaymentMethodDateRange] = useState(
    getStartAndEndOfFiveMonthsAgoToEndOfMonth()
  );
  const [paymentMethod, setPaymentMethod] = useState(1);
  // "Must login with Password" (ACS Dashboard BRD 3 July 2026): the Total Sales
  // and payment-method amount widgets stay masked until the admin re-enters
  // their password. A still-valid elevation token from this tab session keeps
  // them unlocked across visits.
  const [amountsUnlocked, setAmountsUnlocked] = useState(
    () => !!getElevationToken()
  );
  const [unlockModalVisible, setUnlockModalVisible] = useState(false);

  const relockAmounts = () => {
    clearElevation();
    setAmountsUnlocked(false);
    setSalesData({ labels: [], datasets: [] });
    setTotalSales(0);
    setSalesStatus("");
    setPercentageChange(0);
    setTotalPreviousSales(0);
    setPaymentMethodData({ labels: [], datasets: [] });
  };
  const [paymentOutcomeData, setPaymentOutcomeData] = useState(null);
  const [txBreakdownData, setTxBreakdownData] = useState(null);
  const [txBreakdownBy, setTxBreakdownBy] = useState("insurer");
  const [txBreakdownDateRange, setTxBreakdownDateRange] = useState(
    getStartAndEndOfFiveMonthsAgoToEndOfMonth()
  );
  const [policyOutcomeData, setPolicyOutcomeData] = useState(null);
  const [policyOutcomeDateRange, setPolicyOutcomeDateRange] = useState(
    getStartAndEndOfFiveMonthsAgoToEndOfMonth()
  );
  const [txVolumeData, setTxVolumeData] = useState(null);
  const [txVolumeGroupBy, setTxVolumeGroupBy] = useState("month");
  const [txVolumeDateRange, setTxVolumeDateRange] = useState(
    getStartAndEndOfFiveMonthsAgoToEndOfMonth()
  );
  const [txAmountData, setTxAmountData] = useState(null);
  const [txAmountGroupBy, setTxAmountGroupBy] = useState("month");
  const [txAmountSplit, setTxAmountSplit] = useState("product");
  const [txAmountDateRange, setTxAmountDateRange] = useState(
    getStartAndEndOfFiveMonthsAgoToEndOfMonth()
  );

  const formatRangeForApi = (range) => {
    let [rangeStart, rangeEnd] = range || [null, null];

    if (!rangeStart || !rangeEnd) {
      const today = new Date();
      rangeStart = today;
      rangeEnd = today;
    }

    rangeStart.setHours(0, 0, 0, 0);
    rangeEnd.setHours(23, 59, 59, 999);

    const formatDate = (date) => date.toLocaleDateString("en-CA");

    return {
      start_date: formatDate(rangeStart),
      end_date: formatDate(rangeEnd),
    };
  };

  const fetchTxBreakdownData = async () => {
    try {
      const response = await getDashboardTransactionsByInsurerProduct(
        formatRangeForApi(txBreakdownDateRange)
      );
      setTxBreakdownData(response.data);
    } catch (error) {
      console.error("Error fetching transaction breakdown data:", error);
    }
  };

  const fetchPolicyOutcomeData = async () => {
    try {
      const response = await getDashboardPolicyOutcomes(
        formatRangeForApi(policyOutcomeDateRange)
      );
      setPolicyOutcomeData(response.data);
    } catch (error) {
      console.error("Error fetching policy outcome data:", error);
    }
  };

  const fetchTxVolumeData = async () => {
    try {
      const response = await getDashboardTransactionVolume({
        ...formatRangeForApi(txVolumeDateRange),
        group_by: txVolumeGroupBy,
      });
      setTxVolumeData(response.data);
    } catch (error) {
      console.error("Error fetching transaction volume data:", error);
    }
  };

  const fetchTxAmountData = async () => {
    try {
      const response = await getDashboardTransactionAmounts({
        ...formatRangeForApi(txAmountDateRange),
        group_by: txAmountGroupBy,
        split: txAmountSplit,
      });
      setTxAmountData(response.data);
    } catch (error) {
      console.error("Error fetching transaction amount data:", error);
    }
  };

  const fetchPaymentOutcomeData = async () => {
    try {
      const response = await getDashboardPaymentMethodOutcomes(
        formatRangeForApi(paymentMethodDateRange)
      );
      setPaymentOutcomeData(response.data);
    } catch (error) {
      console.error("Error fetching payment method outcome data:", error);
    }
  };

  const fetchProfile = async () => {
    try {
      setProfileLoading(true);
      const response = await getProfile();
      setAdmin(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setAdmin({});
    } finally {
      setProfileLoading(false);
    }
  };

  const fetchCustomData = async () => {
    try {
      let [startDate, endDate] = dateRange || [null, null];

      if (!startDate || !endDate) {
        const today = new Date();
        startDate = today;
        endDate = today;
      }

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      const formatDate = (date) => {
        return date.toLocaleDateString("en-CA");
      };

      const customResponse = await getDashboardRegisteredUsers({
        start_date: formatDate(startDate),
        end_date: formatDate(endDate),
      });

      const monthlyData = customResponse.data.registeredUsers.reduce(
        (acc, item) => {
          const date = new Date(item.date);
          const monthYear = date.toLocaleString("default", {
            month: "short",
            year: "numeric",
          });
          if (!acc[monthYear]) {
            acc[monthYear] = 0;
          }
          acc[monthYear] += item.total_users;
          return acc;
        },
        {}
      );

      setCustomChartData({
        labels: Object.keys(monthlyData),
        datasets: [
          {
            label: "Number of Registered Users",
            data: Object.values(monthlyData),
          },
        ],
      });
      setTotalRegistered(customResponse.data.totalRegistered);
      setRegisterStatus(customResponse.data.registerStatus);
      setRegisterPercentageChange(customResponse.data.percentageChange);
    } catch (error) {
      console.error("Error fetching custom data:", error);
    }
  };

  const fetchCustomLoggedInData = async () => {
    try {
      let [startDate, endDate] = loggedInDateRange || [null, null];

      if (!startDate || !endDate) {
        const today = new Date();
        startDate = today;
        endDate = today;
      }

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      const formatDate = (date) => {
        return date.toLocaleDateString("en-CA");
      };

      const customLoggedInResponse = await getDashboardLoggedInUsers({
        start_date: formatDate(startDate),
        end_date: formatDate(endDate),
      });

      const monthlyLoggedInData =
        customLoggedInResponse.data.loggedInUsers.reduce((acc, item) => {
          const date = new Date(item.date);
          const monthYear = date.toLocaleString("default", {
            month: "short",
            year: "numeric",
          });
          if (!acc[monthYear]) {
            acc[monthYear] = 0;
          }
          acc[monthYear] += item.total_users;
          return acc;
        }, {});

      setCustomLoggedInChartData({
        labels: Object.keys(monthlyLoggedInData),
        datasets: [
          {
            label: "Number of Logged In Users",
            data: Object.values(monthlyLoggedInData),
          },
        ],
      });
      setTotalLoggedIn(customLoggedInResponse.data.totalLoggedIn);
      setLoggedInStatus(customLoggedInResponse.data.loggedInStatus);
      setLoggedInPercentageChange(customLoggedInResponse.data.percentageChange);
    } catch (error) {
      console.error("Error fetching custom logged in data:", error);
    }
  };

  const fetchIncompleteProfiles = async () => {
    try {
      setIncompleteProfilesLoading(true);
      setIncompleteProfilesError(null);
      const response = await getDashboardIncompleteProfileUsers();
      setIncompleteProfiles(response.data);
    } catch (error) {
      console.error("Error fetching incomplete profile users:", error);
      setIncompleteProfilesError(error.message || "An error occurred");
    } finally {
      setIncompleteProfilesLoading(false);
    }
  };

  const fetchLiveLoggedIn = async (showSpinner = true) => {
    try {
      if (showSpinner) {
        setLiveLoggedInLoading(true);
      }
      setLiveLoggedInError(null);
      const response = await getDashboardLiveLoggedInUsers();
      setLiveLoggedIn(response.data);
    } catch (error) {
      console.error("Error fetching live logged in users:", error);
      setLiveLoggedInError(error.message || "An error occurred");
    } finally {
      if (showSpinner) {
        setLiveLoggedInLoading(false);
      }
    }
  };

  const fetchSalesData = async () => {
    try {
      let [startDate, endDate] = salesDateRange || [null, null];

      if (!startDate || !endDate) {
        const today = new Date();
        startDate = today;
        endDate = today;
      }

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      const formatDate = (date) => date.toLocaleDateString("en-CA");

      const response = await getDashboardTotalSales({
        start_date: formatDate(startDate),
        end_date: formatDate(endDate),
      });

      const monthlySalesData = response.data.sales.reduce((acc, item) => {
        const date = new Date(item.date);
        const monthYear = date.toLocaleString("default", {
          month: "short",
          year: "numeric",
        });
        if (!acc[monthYear]) {
          acc[monthYear] = 0;
        }
        acc[monthYear] += item.amount;
        return acc;
      }, {});

      setSalesData({
        labels: Object.keys(monthlySalesData),
        datasets: [
          {
            label: "Sales Amount",
            data: Object.values(monthlySalesData),
            fill: false,
            borderColor: "rgba(75,192,192,1)",
            tension: 0.4,
          },
        ],
      });

      setTotalSales(response.data.totalSales);
      setSalesStatus(response.data.salesStatus);
      setPercentageChange(response.data.percentageChange);
      setTotalPreviousSales(response.data.totalPreviousSales);
    } catch (error) {
      if (error.message === "Password verification required") {
        relockAmounts();
        return;
      }
      console.error("Error fetching sales data:", error);
    }
  };

  const fetchOrdersData = async () => {
    try {
      const sampleData = [
        { category: "Electronics", orders: 120 },
        { category: "Fashion", orders: 80 },
        { category: "Home & Garden", orders: 50 },
        { category: "Sports", orders: 30 },
        { category: "Others", orders: 20 },
      ];
      const ordersDataValues = sampleData.map((item) => item.orders);
      setOrdersData({
        labels: sampleData.map((item) => item.category),
        datasets: [
          {
            label: "Number of Orders",
            data: ordersDataValues,
            backgroundColor: [
              "#FF6384",
              "#36A2EB",
              "#FFCE56",
              "#4BC0C0",
              "#9966FF",
            ],
          },
        ],
      });
    } catch (error) {
      console.error("Error fetching orders data:", error);
    }
  };

  const fetchDashboardTotalOrders = async () => {
    try {
      let [startDate, endDate] = ordersDateRange || [null, null];

      if (!startDate || !endDate) {
        const today = new Date();
        startDate = today;
        endDate = today;
      }

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      const formatDate = (date) => date.toLocaleDateString("en-CA");

      const response = await getDashboardTotalOrders({
        start_date: formatDate(startDate),
        end_date: formatDate(endDate),
      });

      const ordersData = response.data.reduce((acc, item) => {
        item.orders.forEach((order) => {
          let insuranceTypeName;
          switch (order.insurance_type) {
            case 1:
              insuranceTypeName = "Foreign Worker";
              break;
            case 2:
              insuranceTypeName = "Term Life";
              break;
            case 3:
              insuranceTypeName = "Health";
              break;
            case 4:
              insuranceTypeName = "PA";
              break;
            case 5:
              insuranceTypeName = "Motor";
              break;
            case 6:
              insuranceTypeName = "Offshore";
              break;
            case 7:
              insuranceTypeName = "Group";
              break;
            case 8:
              insuranceTypeName = "Property";
              break;
            default:
              insuranceTypeName = "Unknown";
          }
          if (!acc[insuranceTypeName]) {
            acc[insuranceTypeName] = 0;
          }
          acc[insuranceTypeName] += order.total_orders;
        });
        return acc;
      }, {});

      const labels = Object.keys(ordersData);
      const data = Object.values(ordersData);

      setOrdersData({
        labels,
        datasets: [
          {
            label: "Number of Orders",
            data,
            backgroundColor: [
              "#DC2626", // Red
              "#F97316", // Orange
              "#EAB308", // Yellow
              "#16A34A", // Green
              "#1E40AF", // Blue
            ],
          },
        ],
      });
    } catch (error) {
      console.error("Error fetching dashboard total orders:", error);
    }
  };

  const fetchDashboardTotalPolicies = async () => {
    try {
      let [startDate, endDate] = policiesDateRange || [null, null];

      if (!startDate || !endDate) {
        const today = new Date();
        startDate = today;
        endDate = today;
      }

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      const formatDate = (date) => date.toLocaleDateString("en-CA");

      const response = await getDashboardTotalPolicies({
        start_date: formatDate(startDate),
        end_date: formatDate(endDate),
      });

      const monthlyPoliciesData = response.data.reduce((acc, item) => {
        const date = new Date(item.date);
        const monthYear = date.toLocaleString("default", {
          month: "short",
          year: "numeric",
        });
        if (!acc[monthYear]) {
          acc[monthYear] = {
            totalActive: 0,
            totalExpired: 0,
            totalPending: 0,
            totalPendingPolicy: 0,
            totalUpcomingRenewals: 0,
          };
        }
        acc[monthYear].totalActive += item.totalActive;
        acc[monthYear].totalExpired += item.totalExpired;
        acc[monthYear].totalPending += item.totalPending;
        acc[monthYear].totalPendingPolicy += item.totalPendingPolicy;
        acc[monthYear].totalUpcomingRenewals += item.totalUpcomingRenewals;
        return acc;
      }, {});

      setPoliciesData({
        labels: Object.keys(monthlyPoliciesData),
        datasets: [
          {
            label: "Active",
            data: Object.values(monthlyPoliciesData).map(
              (data) => data.totalActive
            ),
            backgroundColor: "#1E40AF", // Blue
          },
          {
            label: "Expired",
            data: Object.values(monthlyPoliciesData).map(
              (data) => data.totalExpired
            ),
            backgroundColor: "#DC2626", // Red
          },
          {
            label: "Pending",
            data: Object.values(monthlyPoliciesData).map(
              (data) => data.totalPending
            ),
            backgroundColor: "#F97316", // Orange
          },
          {
            label: "Pending Policy",
            data: Object.values(monthlyPoliciesData).map(
              (data) => data.totalPendingPolicy
            ),
            backgroundColor: "#EAB308", // Yellow
          },
          {
            label: "Upcoming Renewals",
            data: Object.values(monthlyPoliciesData).map(
              (data) => data.totalUpcomingRenewals
            ),
            backgroundColor: "#16A34A", // Green
          },
        ],
      });
    } catch (error) {
      console.error("Error fetching dashboard total policies:", error);
    }
  };

  const fetchPaymentMethodData = async () => {
    try {
      let [startDate, endDate] = paymentMethodDateRange || [null, null];

      if (!startDate || !endDate) {
        const today = new Date();
        startDate = today;
        endDate = today;
      }

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      const formatDate = (date) => date.toLocaleDateString("en-CA");

      const response = await getDashboardTotalByPaymentMethod({
        start_date: formatDate(startDate),
        end_date: formatDate(endDate),
        payment_method: paymentMethod,
      });

      if (response.data && response.data.transactions) {
        const paymentMethodTotals = response.data.transactions.reduce(
          (acc, item) => {
            item.transactions.forEach((transaction) => {
              const paymentMethod = transaction.payment_method;
              if (!acc[paymentMethod]) {
                acc[paymentMethod] = 0;
              }
              acc[paymentMethod] += transaction.amount;
            });
            return acc;
          },
          {}
        );

        setPaymentMethodData({
          labels: Object.keys(paymentMethodTotals),
          datasets: [
            {
              label: "Total Amount",
              data: Object.values(paymentMethodTotals),
              backgroundColor: [
                "#FF6384",
                "#36A2EB",
                "#FFCE56",
                "#4BC0C0",
                "#9966FF",
              ],
            },
          ],
        });
      } else {
        console.error("Unexpected response data format:", response.data);
      }
    } catch (error) {
      if (error.message === "Password verification required") {
        relockAmounts();
        return;
      }
      console.error("Error fetching payment method data:", error);
    }
  };

  useEffect(() => {
    fetchProfile();
    fetchOrdersData();
    fetchDashboardTotalOrders();
    fetchDashboardTotalPolicies();
    fetchIncompleteProfiles();
    fetchLiveLoggedIn();
    // The password-gated amount widgets (Total Sales, payment-method) only
    // fetch once the admin has unlocked them (see the amountsUnlocked effect).
    setIsLoading(false);
  }, []);

  // Keep the live logged-in counts fresh without a manual reload
  useEffect(() => {
    const interval = setInterval(() => fetchLiveLoggedIn(false), 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch the password-gated amount widgets whenever they become unlocked,
  // and schedule the automatic re-lock for when the elevation expires.
  useEffect(() => {
    if (!amountsUnlocked) return;

    fetchSalesData();
    fetchPaymentMethodData();

    const remainingMs = getElevationRemainingMs();
    if (remainingMs <= 0) {
      relockAmounts();
      return;
    }
    const relockTimer = setTimeout(relockAmounts, remainingMs);
    return () => clearTimeout(relockTimer);
  }, [amountsUnlocked]);

  useEffect(() => {
    if (dateRange && dateRange[0] && dateRange[1]) {
      fetchCustomData();
    } else {
      setDateRange([new Date(), new Date()]);
    }
  }, [dateRange]);

  useEffect(() => {
    if (loggedInDateRange && loggedInDateRange[0] && loggedInDateRange[1]) {
      fetchCustomLoggedInData();
    } else {
      setLoggedInDateRange([new Date(), new Date()]);
    }
  }, [loggedInDateRange]);

  useEffect(() => {
    if (ordersDateRange && ordersDateRange[0] && ordersDateRange[1]) {
      fetchDashboardTotalOrders();
    } else {
      setOrdersDateRange([new Date(), new Date()]);
    }
  }, [ordersDateRange]);

  useEffect(() => {
    if (policiesDateRange && policiesDateRange[0] && policiesDateRange[1]) {
      fetchDashboardTotalPolicies();
    } else {
      setPoliciesDateRange([new Date(), new Date()]);
    }
  }, [policiesDateRange]);

  useEffect(() => {
    if (salesDateRange && salesDateRange[0] && salesDateRange[1]) {
      if (amountsUnlocked) {
        fetchSalesData();
      }
    } else {
      setSalesDateRange([new Date(), new Date()]);
    }
  }, [salesDateRange]);

  useEffect(() => {
    if (
      paymentMethodDateRange &&
      paymentMethodDateRange[0] &&
      paymentMethodDateRange[1]
    ) {
      if (amountsUnlocked) {
        fetchPaymentMethodData();
      }
    } else {
      setPaymentMethodDateRange([new Date(), new Date()]);
    }
  }, [paymentMethodDateRange, paymentMethod]);

  useEffect(() => {
    if (
      paymentMethodDateRange &&
      paymentMethodDateRange[0] &&
      paymentMethodDateRange[1]
    ) {
      fetchPaymentOutcomeData();
    }
  }, [paymentMethodDateRange]);

  useEffect(() => {
    if (
      txBreakdownDateRange &&
      txBreakdownDateRange[0] &&
      txBreakdownDateRange[1]
    ) {
      fetchTxBreakdownData();
    } else {
      setTxBreakdownDateRange([new Date(), new Date()]);
    }
  }, [txBreakdownDateRange]);

  useEffect(() => {
    if (
      policyOutcomeDateRange &&
      policyOutcomeDateRange[0] &&
      policyOutcomeDateRange[1]
    ) {
      fetchPolicyOutcomeData();
    } else {
      setPolicyOutcomeDateRange([new Date(), new Date()]);
    }
  }, [policyOutcomeDateRange]);

  useEffect(() => {
    if (txVolumeDateRange && txVolumeDateRange[0] && txVolumeDateRange[1]) {
      fetchTxVolumeData();
    } else {
      setTxVolumeDateRange([new Date(), new Date()]);
    }
  }, [txVolumeDateRange, txVolumeGroupBy]);

  useEffect(() => {
    if (txAmountDateRange && txAmountDateRange[0] && txAmountDateRange[1]) {
      fetchTxAmountData();
    } else {
      setTxAmountDateRange([new Date(), new Date()]);
    }
  }, [txAmountDateRange, txAmountGroupBy, txAmountSplit]);

  return (
    <>
      {isLoading || profileLoading ? (
        <Skeletons />
      ) : (
        <div
          className="dashboard-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: "20px",
            gridTemplateRows: "auto auto auto",
          }}
        >
          <div style={{ display: "grid", gap: "20px" }}>
            <WelcomeCard admin={admin} navigate={navigate} />
            <SalesCard
              salesData={salesData}
              dateRange={salesDateRange}
              setDateRange={setSalesDateRange}
              totalSales={totalSales}
              salesStatus={salesStatus}
              percentageChange={percentageChange}
              totalPreviousSales={totalPreviousSales}
              locked={!amountsUnlocked}
              onUnlock={() => setUnlockModalVisible(true)}
            />
          </div>
          <div style={{ display: "grid", gap: "20px" }}>
            <RegisteredUsersCard
              customChartData={customChartData}
              dateRange={dateRange}
              setDateRange={setDateRange}
              customLoggedInChartData={customLoggedInChartData}
              loggedInDateRange={loggedInDateRange}
              setLoggedInDateRange={setLoggedInDateRange}
              totalRegistered={totalRegistered}
              registerStatus={registerStatus}
              registerPercentageChange={registerPercentageChange}
              totalLoggedIn={totalLoggedIn}
              loggedInStatus={loggedInStatus}
              loggedInPercentageChange={loggedInPercentageChange}
            />
          </div>
          <div style={{ display: "grid", gap: "20px" }}>
            <LiveLoggedInUsersCard
              data={liveLoggedIn}
              loading={liveLoggedInLoading}
              error={liveLoggedInError}
              onRefresh={fetchLiveLoggedIn}
            />
            <IncompleteProfilesCard
              data={incompleteProfiles}
              loading={incompleteProfilesLoading}
              error={incompleteProfilesError}
            />
          </div>
          <div style={{ display: "grid", gap: "20px" }}>
            <PoliciesCard
              policiesData={policiesData}
              dateRange={policiesDateRange}
              setDateRange={setPoliciesDateRange}
            />
          </div>
          <div style={{ display: "grid", gap: "20px" }}>
            <OrdersCard
              ordersData={ordersData}
              dateRange={ordersDateRange}
              setDateRange={setOrdersDateRange}
            />
          </div>
          <div style={{ display: "grid", gap: "20px" }}>
            <TransactionBreakdownCard
              breakdownData={txBreakdownData}
              dateRange={txBreakdownDateRange}
              setDateRange={setTxBreakdownDateRange}
              breakdownBy={txBreakdownBy}
              setBreakdownBy={setTxBreakdownBy}
            />
          </div>
          <div style={{ display: "grid", gap: "20px" }}>
            <PolicyOutcomeCard
              outcomeData={policyOutcomeData}
              dateRange={policyOutcomeDateRange}
              setDateRange={setPolicyOutcomeDateRange}
            />
          </div>
          <div style={{ display: "grid", gap: "20px", gridColumn: "span 2" }}>
            <TransactionVolumeCard
              volumeData={txVolumeData}
              dateRange={txVolumeDateRange}
              setDateRange={setTxVolumeDateRange}
              groupBy={txVolumeGroupBy}
              setGroupBy={setTxVolumeGroupBy}
            />
          </div>
          <div style={{ display: "grid", gap: "20px", gridColumn: "span 2" }}>
            <TransactionAmountCard
              amountData={txAmountData}
              dateRange={txAmountDateRange}
              setDateRange={setTxAmountDateRange}
              groupBy={txAmountGroupBy}
              setGroupBy={setTxAmountGroupBy}
              split={txAmountSplit}
              setSplit={setTxAmountSplit}
            />
          </div>
          <div style={{ display: "grid", gap: "20px", gridColumn: "span 2" }}>
            <PaymentMethodCard
              paymentMethodData={paymentMethodData}
              setPaymentMethodData={setPaymentMethodData}
              dateRange={paymentMethodDateRange}
              setDateRange={setPaymentMethodDateRange}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              paymentOutcomeData={paymentOutcomeData}
              locked={!amountsUnlocked}
              onUnlock={() => setUnlockModalVisible(true)}
            />
          </div>
          <div style={{ display: "grid", gap: "20px", gridColumn: "span 2" }}>
            <QuotationFunnelCard
              title="API Request Quotation (Vehicle)"
              funnel="api_vehicle"
            />
          </div>
          <div style={{ display: "grid", gap: "20px", gridColumn: "span 2" }}>
            <QuotationFunnelCard
              title="Manual Request Quotation (Vehicle)"
              funnel="manual_vehicle"
            />
          </div>
          <div style={{ display: "grid", gap: "20px", gridColumn: "span 2" }}>
            <QuotationFunnelCard
              title="Manual Request Quotation (Inquiry & URL)"
              funnel="manual_inquiry"
            />
          </div>
          <UnlockAmountsModal
            visible={unlockModalVisible}
            onClose={() => setUnlockModalVisible(false)}
            onUnlocked={() => {
              setUnlockModalVisible(false);
              setAmountsUnlocked(true);
            }}
          />
          <style jsx>{`
            @media (max-width: 768px) {
              .dashboard-grid {
                grid-template-columns: 1fr !important;
              }
              .dashboard-grid > div {
                grid-column: span 2 !important;
              }
              .border-light {
                margin: 0 !important;
                padding: 0 !important;
              }
            }
          `}</style>
        </div>
      )}
    </>
  );
};

export default Dashboard;
